import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Card, Container, Row, Col, Spinner, Form, Badge, Button, Alert } from 'react-bootstrap';
import { NavSidebar } from './components/NavSidebar';
import { useAuth } from '../context/AuthContext';
import { useFilterContext } from '../context/SharedFilterContext';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import DataTable from 'react-data-table-component';
import WorkOrders from '../models/WorkOrders';
import Company from '../models/Company';
import pLimit from 'p-limit';
import { FaCalendarAlt, FaFilter, FaFileExport, FaSync, FaSave, FaChartBar, FaDownload } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import {
  BrowserRouter as Router,
  useLocation,
  useHistory
} from "react-router-dom";
import {MdArrowBack} from 'react-icons/md';
import html2canvas from 'html2canvas';
import User from '../models/User';
import PropertyList from '../models/PropertyList';

const CostAvoidanceMetricsScreen = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [chartLoading, setChartLoading] = useState(true);
  const [chartDownloadLoading, setChartDownloadLoading] = useState(false);

  // Get property list state from SharedFilterContext
  const {
    inspectionViewMode: viewMode,
    setInspectionViewMode: setViewMode,
    selectedPropertyList,
    setSelectedPropertyList,
    availablePropertyLists,
    setAvailablePropertyLists
  } = useFilterContext();

  // Add new state for favorited properties functionality
  const [currentUserData, setCurrentUserData] = useState(null);
  const [hasFavorites, setHasFavorites] = useState(false);
  const [allWorkOrders, setAllWorkOrders] = useState([]); // Store all work orders
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]); // Store filtered work orders

  // Property list state variables (keeping hasPropertyLists local as it's UI-specific)
  const [hasPropertyLists, setHasPropertyLists] = useState(false);

  // State for date range
  const [startDate, setStartDate] = useState(moment().subtract(3, 'month').startOf('day').toDate());
  const [endDate, setEndDate] = useState(moment().startOf('day').toDate());

  // Temporary state for date range
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const [corrigoWorkOrders, setCorrigoWorkOrders] = useState([]);
  const [error, setError] = useState(null);
  const [paginationPerPage, setPaginationPerPage] = useState(100);
  
  // State for data assumptions - initialize to 0
  const [copelandDiscusCost, setCopelandDiscusCost] = useState(0);
  const [carlyleShcCost, setCarlyleShcCost] = useState(0);
  const [copelandScrollCost, setCopelandScrollCost] = useState(0);

  // Constants for chunking and concurrency
  const CHUNK_SIZE = 100;
  const MAX_CONCURRENCY = 6;

  const limit = pLimit(MAX_CONCURRENCY);

  // Memoize the fetchChunk function to prevent unnecessary recreations
  const fetchChunk = useCallback(async (ids) => {
    const searchList = [{ field: 'work_order_id', value: ids, comparison: 'IN' }];
    const { data } = await WorkOrders.filterBy(
      searchList, 'dt_created', 'desc', 10000, undefined, undefined, undefined, true
    );
    return data;
  }, []);

  // Add function to fetch current user data with fresh favorite_properties
  const fetchCurrentUserData = async () => {
    try {
      const freshUserData = await User.findByUUID(user.uuid);
      setCurrentUserData(freshUserData);
      
      // Check if user has favorites and parse JSON
      let favoriteUuids = [];
      if (freshUserData?.favorite_properties) {
        try {
          const parsedFavorites = JSON.parse(freshUserData.favorite_properties);
          favoriteUuids = Array.isArray(parsedFavorites) ? parsedFavorites : [];
        } catch (parseError) {
          console.warn('Error parsing favorite_properties:', parseError);
          favoriteUuids = [];
        }
      }
      
      const hasUserFavorites = favoriteUuids.length > 0;
      setHasFavorites(hasUserFavorites);
      
      return { freshUserData, favoriteUuids };
    } catch (error) {
      console.error('Error fetching user data:', error);
      setHasFavorites(false);
      return { freshUserData: null, favoriteUuids: [] };
    }
  };

  // Add function to filter work orders based on view mode
  const applyWorkOrderFiltering = async (workOrders, mode) => {
    console.log(`\n========== [CostAvoidanceMetricsScreen] FILTERING - MODE: ${mode} ==========`);
    console.log(`Total work orders before filtering: ${workOrders.length}`);

    // Log sample of work order property_uuid values for debugging
    const sampleWorkOrders = workOrders.slice(0, 5);
    console.log("Sample work orders with property_uuid:");
    sampleWorkOrders.forEach((wo, index) => {
      console.log(`  WO ${index + 1}: work_order_id=${wo.work_order_id}, property_uuid=${wo.property_uuid}, property_number=${wo.property_number}`);
    });

    if (mode === 'favorited') {
      console.log("\n--- FAVORITED MODE ---");
      const { favoriteUuids } = await fetchCurrentUserData();

      console.log(`Number of favorited property UUIDs: ${favoriteUuids.length}`);
      if (favoriteUuids.length <= 10) {
        console.log("Favorited property UUIDs:", favoriteUuids);
      }

      if (favoriteUuids.length === 0) {
        console.log("No favorites found, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in user's favorites
      const favoritedWorkOrders = workOrders.filter(workOrder =>
        workOrder.property_uuid && favoriteUuids.includes(workOrder.property_uuid)
      );

      console.log(`Filtered ${favoritedWorkOrders.length} work orders for favorited properties`);
      setFilteredWorkOrders(favoritedWorkOrders);

    } else if (mode === 'propertyList') {
      console.log("\n--- PROPERTY LIST MODE ---");

      if (!selectedPropertyList) {
        console.log("No property list selected, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      console.log(`Selected property list: ${selectedPropertyList.name} (UUID: ${selectedPropertyList.uuid})`);

      // Parse the property list UUIDs
      let propertyUuids = [];
      try {
        propertyUuids = JSON.parse(selectedPropertyList.properties_list);
        console.log(`Successfully parsed property list with ${propertyUuids.length} properties`);
        if (propertyUuids.length <= 10) {
          console.log("Property UUIDs in list:", propertyUuids);
        }
      } catch (e) {
        console.error('Error parsing property list:', e);
        setFilteredWorkOrders([]);
        return;
      }

      if (propertyUuids.length === 0) {
        console.log("Property list is empty, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in the selected property list
      const propertyListWorkOrders = workOrders.filter(workOrder =>
        workOrder.property_uuid && propertyUuids.includes(workOrder.property_uuid)
      );

      console.log(`Filtered ${propertyListWorkOrders.length} work orders for property list "${selectedPropertyList.name}"`);

      // Additional debugging: Check if any property list UUIDs match work order UUIDs
      const matchingUuids = propertyUuids.filter(uuid =>
        workOrders.some(wo => wo.property_uuid === uuid)
      );
      console.log(`Property UUIDs from list that have work orders: ${matchingUuids.length}`);

      setFilteredWorkOrders(propertyListWorkOrders);

    } else {
      console.log("\n--- ALL MODE ---");
      console.log(`Showing all ${workOrders.length} work orders`);
      setFilteredWorkOrders(workOrders);
    }

    console.log(`========== END FILTERING ==========\n`);
  };

  const fetchFilteredWorkOrders = useCallback(async () => {
    console.log("[CostAvoidanceMetricsScreen] Fetching filtered work orders");
    setIsLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Fetch work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(endDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
      );

      console.log("Work Order IDs by Date Range:", workOrderIds);

      if (workOrderIds.length === 0) {
        setAllWorkOrders([]);
        setFilteredWorkOrders([]);
        setIsLoading(false);
        return;
      }

      // Break work order IDs into chunks of CHUNK_SIZE
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel, limited by p-limit
      let allWorkOrders = [];
      await Promise.all(chunks.map(chunk => 
        limit(() => fetchChunk(chunk).then(res => allWorkOrders.push(...res)))
      ));

      // Apply existing filtering logic
      allWorkOrders = allWorkOrders.filter(wo => 
        wo.status_id === 'Completed' && 
        wo.type_category !== 'PMRM' && 
        wo.source !== 'workzone_area_match_historical_context' &&
        wo.avoid !== 1  // Filter out work orders with avoid = 1
      );

      // Explicitly sort work orders by dt_created in descending order
      allWorkOrders.sort((a, b) => new Date(b.dt_created) - new Date(a.dt_created));
      console.log("This is the sorted work orders", allWorkOrders);
      
      // Store all work orders
      setAllWorkOrders(allWorkOrders);
      
      // Apply filtering based on current view mode
      await applyWorkOrderFiltering(allWorkOrders, viewMode);
      
    } catch (err) {
      console.error("[CostAvoidanceMetricsScreen] Error fetching work orders:", err);
      setError(err.message || "Failed to fetch work orders");
    } finally {
      setIsLoading(false);
      const endTime = Date.now();
      const durationInSeconds = (endTime - startTime) / 1000;
      console.log(`[CostAvoidanceMetricsScreen] Time taken to fetch all work orders: ${durationInSeconds} seconds`);
    }
  }, [fetchChunk, startDate, endDate, viewMode, selectedPropertyList]);

  // Add function to handle view mode toggle
  const handleViewModeToggle = async (mode) => {
    setViewMode(mode);

    // Clear property list when switching away from propertyList mode
    if (mode !== 'propertyList') {
      setSelectedPropertyList(null);
    }

    // Apply filtering to existing work orders
    await applyWorkOrderFiltering(allWorkOrders, mode);
  };
  
  // Handle property list selection
  const handlePropertyListSelect = useCallback(async (list) => {
    setSelectedPropertyList(list);
    setViewMode('propertyList');
    
    // Apply filtering with the new list
    if (!list) {
      setFilteredWorkOrders([]);
      return;
    }
    
    let propertyUuids = [];
    try {
      propertyUuids = JSON.parse(list.properties_list);
    } catch (e) {
      console.error('Error parsing property list:', e);
      setFilteredWorkOrders([]);
      return;
    }
    
    const propertyListWorkOrders = allWorkOrders.filter(workOrder => 
      workOrder.property_uuid && propertyUuids.includes(workOrder.property_uuid)
    );
    
    setFilteredWorkOrders(propertyListWorkOrders);
  }, [allWorkOrders]);

  // Function to fetch property lists
  const fetchPropertyLists = async () => {
    try {
      // Check if the user's company is Meijer (company_id 1121)
      if (user?.company?.id === "1121") {
        const lists = await PropertyList.getPropertyListsByCompany(1121);
        console.log('Fetched property lists:', lists);
        setAvailablePropertyLists(lists || []);
        setHasPropertyLists(lists && lists.length > 0);
      } else {
        setAvailablePropertyLists([]);
        setHasPropertyLists(false);
      }
    } catch (error) {
      console.error('Error fetching property lists:', error);
      setAvailablePropertyLists([]);
      setHasPropertyLists(false);
    }
  };

  // Fetch user data on component mount
  useEffect(() => {
    if (user?.uuid) {
      fetchCurrentUserData();
    }
  }, [user?.uuid]);
  
  // Fetch property lists on component mount
  useEffect(() => {
    if (user?.company?.id) {
      fetchPropertyLists();
    }
  }, [user?.company?.id]);

  // Fetch work orders when the component mounts or date range changes
  useEffect(() => {
    fetchFilteredWorkOrders();
  }, [fetchFilteredWorkOrders]);

  // Update corrigoWorkOrders when filteredWorkOrders changes
  useEffect(() => {
    setCorrigoWorkOrders(filteredWorkOrders);
  }, [filteredWorkOrders]);

  const applyDateRange = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  // Function to decode data assumptions from JSON
  const decodeDataAssumptions = (dataAssumptionsStr) => {
    try {
      return JSON.parse(dataAssumptionsStr);
    } catch (error) {
      console.error("Error decoding data assumptions:", error);
      return {
        copelandDiscusCost: 0,
        carlyleShcCost: 0,
        copelandScrollCost: 0
      };
    }
  };

  // Function to encode data assumptions to JSON
  const encodeDataAssumptions = () => {
    return JSON.stringify({
      copelandDiscusCost,
      carlyleShcCost,
      copelandScrollCost
    });
  };

  // New state to track if cost assumptions are fully loaded
  const [costAssumptionsLoaded, setCostAssumptionsLoaded] = useState(false);

  // Fetch company data assumptions
  useEffect(() => {
    const fetchCompanyDataAssumptions = async () => {
      try {
        const companyArray = await Company.searchByCompanyUuid(user?.company?.uuid);
        const company = companyArray?.length > 0 ? companyArray[0] : null;
        
        if (company && company?.data_assumptions) {
          const dataAssumptions = decodeDataAssumptions(company?.data_assumptions);
          setCopelandDiscusCost(dataAssumptions.copelandDiscusCost || 0);
          setCarlyleShcCost(dataAssumptions.carlyleShcCost || 0);
          setCopelandScrollCost(dataAssumptions.copelandScrollCost || 0);
          setCostAssumptionsLoaded(true); // Mark as loaded
        } else {
          // Even if no data assumptions, mark as loaded so chart can proceed with 0 values
          setCostAssumptionsLoaded(true);
        }
      } catch (error) {
        console.error("Error fetching company data assumptions:", error);
        setCostAssumptionsLoaded(true); // Mark as loaded even on error
      }
    };

    if (user?.company?.uuid) {
      fetchCompanyDataAssumptions();
    }
  }, [user?.company?.uuid]);

  const costAvoidanceColumns = useMemo(() => [
    { 
      name: (
        <div style={{ lineHeight: '1.2', textAlign: 'center' }}>
          Positive<br />Validation
        </div>
      ), 
      selector: row => row.validation, 
      sortable: true, 
      width: '100px',
      cell: row => {
        const validationMap = {
          1: { label: '1', variant: 'success' },
          2: { label: '2', variant: 'danger' },
          3: { label: '3', variant: 'warning' }
        };
        
        const validation = validationMap[row.validation];
        
        return validation ? (
          <Badge variant={validation.variant}>
            {validation.label}
          </Badge>
        ) : (
          <span className="text-muted">-</span>
        );
      }
    },
    { 
      name: 'Asset', 
      selector: row => row.asset, 
      sortable: true, 
      width: '140px',
      cell: row => {
        const assetMap = {
          48: 'Copeland Discus',
          49: 'Carlyle SHC',
          23: 'Copeland Scroll'
        };
        
        return (
          <span>
            {row.asset ? assetMap[row.asset] : '-'}
          </span>
        );
      }
    },
    { 
      name: 'DT Created', 
      selector: row => row.dt_created, 
      sortable: true, 
      width: '130px',
      format: row => moment(row.dt_created).format('MMM DD, YYYY')
    },
    { name: 'Number', selector: row => row.number, sortable: true, width: '140px' },
    { 
      name: 'DST Equipment', 
      selector: row => row.root_comp, 
      sortable: true, 
      width: '200px',
      cell: row => (
        <div
          title={row.root_comp}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.3',
            maxHeight: '3.9em',
            wordBreak: 'break-word'
          }}
        >
          {row.root_comp}
        </div>
      )
    },
    { 
      name: 'Task Refinement',
      selector: row => row.task_refinement,
      sortable: true,
      width: '525px',
      cell: row => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.3',
            padding: '8px 4px'
          }}
        >
          {row.task_refinement}
        </div>
      )
    },
    { name: 'Status', selector: row => row.status_id, sortable: true, width: '100px' },
    { 
      name: 'Completion Note Text', 
      selector: row => row.completion_note_text, 
      sortable: true, 
      width: '450px',
      cell: row => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.3',
            padding: '8px 4px'
          }}
        >
          {row.completion_note_text || 'N/A'}
        </div>
      )
    },
    { name: 'Property', selector: row => row.property_number, sortable: true, width: '130px' },

    { name: 'Assignee', selector: row => row.assignee_screen_name, sortable: true, width: '150px' },
    { 
      name: 'DT Due', 
      selector: row => row.dt_due, 
      sortable: true, 
      width: '130px',
      format: row => row.dt_due ? moment(row.dt_due).format('MMM DD, YYYY') : 'N/A'
    },
    { name: 'Type Category', selector: row => row.type_category, sortable: true, width: '150px' },
    { name: 'Subtype Name', selector: row => row.subtype_name, sortable: true, width: '150px' },
    { 
      name: 'Costs Total', 
      selector: row => row.costs_total_value, 
      sortable: true, 
      width: '150px',
      right: true,
      format: row => row.costs_total_value ? `$${parseFloat(row.costs_total_value).toFixed(2)}` : '$0.00'
    },
    { 
      name: "Opportunity $'s", 
      selector: row => {
        if (!row.asset) return 0;
        if (row.asset === 48) return copelandDiscusCost;
        if (row.asset === 49) return carlyleShcCost;
        if (row.asset === 23) return copelandScrollCost;
        return 0;
      }, 
      sortable: true, 
      width: '160px',
      right: true,
      format: row => {
        if (!row.asset) return '$0.00';
        let value = 0;
        if (row.asset === 48) value = copelandDiscusCost;
        if (row.asset === 49) value = carlyleShcCost;
        if (row.asset === 23) value = copelandScrollCost;
        return `$${value.toFixed(2)}`;
      }
    },
    { 
      name: 'Cost Avoided (Confirmed)', 
      selector: row => {
        if (row.validation !== 1) return 0;
        
        if (!row.asset) return 0;
        if (row.asset === 48) return copelandDiscusCost;
        if (row.asset === 49) return carlyleShcCost;
        if (row.asset === 23) return copelandScrollCost;
        return 0;
      }, 
      sortable: true, 
      width: '240px',
      right: true,
      format: row => {
        if (row.validation !== 1) return '$0.00';
        
        let value = 0;
        if (row.asset === 48) value = copelandDiscusCost;
        if (row.asset === 49) value = carlyleShcCost;
        if (row.asset === 23) value = copelandScrollCost;
        return `$${value.toFixed(2)}`;
      }
    },
    { 
      name: 'Cost Incurred', 
      selector: row => parseFloat(row.costs_total_value || 0), 
      sortable: true, 
      width: '140px',
      right: true,
      format: row => `$${parseFloat(row.costs_total_value || 0).toFixed(2)}`
    },
    { 
      name: 'Net Cost Avoided (Confirmed)', 
      selector: row => {
        const costIncurred = parseFloat(row.costs_total_value || 0);
        
        let costAvoidedConfirmed = 0;
        if (row.validation === 1 && row.asset) {
          if (row.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
          if (row.asset === 49) costAvoidedConfirmed = carlyleShcCost;
          if (row.asset === 23) costAvoidedConfirmed = copelandScrollCost;
        }
        
        return costAvoidedConfirmed - costIncurred;
      }, 
      sortable: true, 
      width: '300px',
      right: true,
      format: row => {
        const costIncurred = parseFloat(row.costs_total_value || 0);
        
        let costAvoidedConfirmed = 0;
        if (row.validation === 1 && row.asset) {
          if (row.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
          if (row.asset === 49) costAvoidedConfirmed = carlyleShcCost;
          if (row.asset === 23) costAvoidedConfirmed = copelandScrollCost;
        }
        
        return `$${(costAvoidedConfirmed - costIncurred).toFixed(2)}`;
      }
    },
  ], [copelandDiscusCost, carlyleShcCost, copelandScrollCost]);

  // Calculate cumulative cost avoidance metrics by asset type
  const costAvoidanceCalculations = useMemo(() => {
    const calculations = {
      copelandDiscus: { cumulative: 0, count: 0 },
      carlyleShc: { cumulative: 0, count: 0 },
      copelandScroll: { cumulative: 0, count: 0 }
    };

    corrigoWorkOrders.forEach(wo => {
      if (!wo.asset) return;

      const costIncurred = parseFloat(wo.costs_total_value || 0);
      
      let costAvoidedConfirmed = 0;
      if (wo.validation === 1 && wo.asset) {
        if (wo.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
        if (wo.asset === 49) costAvoidedConfirmed = carlyleShcCost;
        if (wo.asset === 23) costAvoidedConfirmed = copelandScrollCost;
      }

      const netCostAvoided = costAvoidedConfirmed - costIncurred;

      if (wo.asset === 48) {
        calculations.copelandDiscus.cumulative += netCostAvoided;
        calculations.copelandDiscus.count += 1;
      } else if (wo.asset === 49) {
        calculations.carlyleShc.cumulative += netCostAvoided;
        calculations.carlyleShc.count += 1;
      } else if (wo.asset === 23) {
        calculations.copelandScroll.cumulative += netCostAvoided;
        calculations.copelandScroll.count += 1;
      }
    });

    return calculations;
  }, [corrigoWorkOrders, copelandDiscusCost, carlyleShcCost, copelandScrollCost]);

  // Calculate total cumulative and count
  const totalCalculations = useMemo(() => {
    const total = {
      cumulative: costAvoidanceCalculations.copelandDiscus.cumulative + 
                  costAvoidanceCalculations.carlyleShc.cumulative + 
                  costAvoidanceCalculations.copelandScroll.cumulative,
      count: costAvoidanceCalculations.copelandDiscus.count + 
             costAvoidanceCalculations.carlyleShc.count + 
             costAvoidanceCalculations.copelandScroll.count
    };
    return total;
  }, [costAvoidanceCalculations]);

  // Custom styles for DataTable
  const tableCustomStyles = {
    header: {
      style: {
        minHeight: '56px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
      },
    },
    headRow: {
      style: {
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        backgroundColor: '#f0f4f8',
        borderBottom: '1px solid #dfe3e8',
      },
    },
    headCells: {
      style: {
        fontWeight: '600',
        color: '#212b36',
        fontSize: '14px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        padding: '12px 16px',
        fontSize: '13px',
      },
    },
    pagination: {
      style: {
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        backgroundColor: '#f8fafc',
      },
      pageButtonsStyle: {
        borderRadius: '4px',
        height: '32px',
        width: '32px',
      },
    },
  };

  // Export calculation summary as CSV
  const handleExportCalculation = () => {
    setExportLoading(true);
    
    const headers = ['Asset Type', 'Cumulative ($)', 'Number of Work Orders'];
    
    const rows = [
      ['Copeland Discus', costAvoidanceCalculations.copelandDiscus.cumulative.toFixed(2), costAvoidanceCalculations.copelandDiscus.count],
      ['Copeland Scroll', costAvoidanceCalculations.copelandScroll.cumulative.toFixed(2), costAvoidanceCalculations.copelandScroll.count],
      ['Carlyle Reed SHC', costAvoidanceCalculations.carlyleShc.cumulative.toFixed(2), costAvoidanceCalculations.carlyleShc.count],
      ['Total', totalCalculations.cumulative.toFixed(2), totalCalculations.count]
    ];
    
    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cost-avoidance-calculations-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => setExportLoading(false), 1000);
  };

  // Export detailed work orders as CSV
  const handleExportDetails = () => {
    setExportLoading(true);
    
    // Get headers from costAvoidanceColumns
    const headers = costAvoidanceColumns.map(column => {
      // Handle JSX column names
      if (typeof column.name === 'object') {
        return 'Positive Validation'; // Special case for the JSX header
      }
      return column.name;
    });
    
    // Create CSV rows using the same data and formatting as displayed in the table
    const rows = corrigoWorkOrders.map(row => {
      return costAvoidanceColumns.map(column => {
        // Handle different data formats based on column
        if (column.format && typeof column.format === 'function') {
          // Use the format function to get formatted value
          return `"${column.format(row).replace(/"/g, '""')}"`;
        } else if (column.selector === (row => row.validation)) {
          // Get validation value for Positive Validation column
          return `"${row.validation || ''}"`;
        } else if (column.name === 'Asset') {
          // Map asset IDs to names
          const assetMap = {
            48: 'Copeland Discus',
            49: 'Carlyle SHC',
            23: 'Copeland Scroll'
          };
          return `"${row.asset ? assetMap[row.asset] || '' : ''}"`;
        } else if (column.selector && typeof column.selector === 'function') {
          // Use selector function to get value
          const value = column.selector(row);
          return value !== undefined && value !== null ? `"${String(value).replace(/"/g, '""')}"` : '""';
        }
        // Default to empty string if no value
        return '""';
      }).join(',');
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cost-avoidance-details-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => setExportLoading(false), 1000);
  };

  // New state for chart data
  const [chartData, setChartData] = useState([]);

  // Process work orders into monthly data (NOT cumulative) - make this a useCallback
  const processChartData = useCallback((workOrders) => {
    console.log("Processing chart data with cost assumptions:", {
      copelandDiscusCost,
      carlyleShcCost,
      copelandScrollCost,
      workOrdersCount: workOrders.length
    });

    // Safeguard: Don't process if cost assumptions aren't properly loaded
    if (copelandDiscusCost === undefined || carlyleShcCost === undefined || copelandScrollCost === undefined) {
      console.warn("Cost assumptions not loaded, skipping chart processing");
      return;
    }

    // Group work orders by month
    const monthlyData = {};
    
    workOrders.forEach(wo => {
      if (!wo.asset) return;

      const month = moment(wo.dt_created).format('YYYY-MM');
      
      if (!monthlyData[month]) {
        monthlyData[month] = {
          copelandDiscus: 0,
          carlyleShc: 0,
          copelandScroll: 0
        };
      }

      const costIncurred = parseFloat(wo.costs_total_value || 0);
      
      let costAvoidedConfirmed = 0;
      if (wo.validation === 1 && wo.asset) {
        if (wo.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
        if (wo.asset === 49) costAvoidedConfirmed = carlyleShcCost;
        if (wo.asset === 23) costAvoidedConfirmed = copelandScrollCost;
      }

      const netCostAvoided = costAvoidedConfirmed - costIncurred;

      if (wo.asset === 48) {
        monthlyData[month].copelandDiscus += netCostAvoided;
      } else if (wo.asset === 49) {
        monthlyData[month].carlyleShc += netCostAvoided;
      } else if (wo.asset === 23) {
        monthlyData[month].copelandScroll += netCostAvoided;
      }
    });

    // Convert to array - each month shows ONLY that month's total
    const sortedMonths = Object.keys(monthlyData).sort();
    const chartDataArray = [];

    sortedMonths.forEach(month => {
      const monthData = {
        month: moment(month).format('MMM YYYY'),
        'Copeland Discus': Math.round(monthlyData[month].copelandDiscus),
        'Carlyle SHC': Math.round(monthlyData[month].carlyleShc),
        'Copeland Scroll': Math.round(monthlyData[month].copelandScroll)
      };
      
      // Debug log for months with large negative values
      if (monthData['Copeland Discus'] < -10000 || monthData['Carlyle SHC'] < -10000 || monthData['Copeland Scroll'] < -10000) {
        console.warn("Month with large negative values:", monthData);
      }
      
      chartDataArray.push(monthData);
    });

    console.log("Final chart data:", chartDataArray);
    setChartData(chartDataArray);
  }, [copelandDiscusCost, carlyleShcCost, copelandScrollCost]);

  // Update fetchHistoricalWorkOrders to use the memoized processChartData
  const fetchHistoricalWorkOrders = useCallback(async () => {
    console.log("[CostAvoidanceMetricsScreen] Fetching historical work orders for chart");
    setChartLoading(true);

    try {
      // Hard-coded date range from July 1, 2024 to current date
      const historicalStartDate = moment('2024-07-01').format('YYYY-MM-DD HH:mm:ss');
      const historicalEndDate = moment().add(1, 'day').format('YYYY-MM-DD HH:mm:ss');

      // Fetch work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        historicalStartDate,
        historicalEndDate
      );

      console.log("Historical Work Order IDs:", workOrderIds.length);

      if (workOrderIds.length === 0) {
        setChartData([]);
        setChartLoading(false);
        return;
      }

      // Break work order IDs into chunks
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel
      let allWorkOrders = [];
      await Promise.all(chunks.map(chunk => 
        limit(() => fetchChunk(chunk).then(res => allWorkOrders.push(...res)))
      ));

      // Apply same filtering as main table
      allWorkOrders = allWorkOrders.filter(wo => 
        wo.status_id === 'Completed' && 
        wo.type_category !== 'PMRM' && 
        wo.source !== 'workzone_area_match_historical_context' &&
        wo.avoid !== 1  // Filter out work orders with avoid = 1
      );

      allWorkOrders.sort((a, b) => new Date(b.dt_created) - new Date(a.dt_created));

      // Process data for chart using the memoized function
      processChartData(allWorkOrders);

    } catch (err) {
      console.error("[CostAvoidanceMetricsScreen] Error fetching historical work orders:", err);
    } finally {
      setChartLoading(false);
    }
  }, [fetchChunk, processChartData]); // Add processChartData as dependency

  // Fetch historical data once on component mount
  useEffect(() => {
    // Only fetch when user is loaded AND cost assumptions are fully loaded
    if (user?.company?.uuid && costAssumptionsLoaded) {
      console.log("Fetching historical data with cost assumptions:", {
        copelandDiscusCost,
        carlyleShcCost,
        copelandScrollCost
      });
      fetchHistoricalWorkOrders();
    }
  }, [user?.company?.uuid, costAssumptionsLoaded, fetchHistoricalWorkOrders]);

  // Add ref for the chart container
  const chartRef = useRef(null);

  // Add function to handle chart download
  const handleDownloadChart = async () => {
    if (!chartRef.current) return;
    
    setChartDownloadLoading(true);
    
    try {
      const canvas = await html2canvas(chartRef.current, {
        backgroundColor: 'white',
        scale: 2, // Higher resolution
        logging: false,
        useCORS: true,
        allowTaint: true
      });
      
      // Create download link
      const link = document.createElement('a');
      link.download = `cost-avoidance-chart-${moment().format('YYYY-MM-DD')}.png`;
      link.href = canvas.toDataURL('image/png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading chart:', error);
    } finally {
      setChartDownloadLoading(false);
    }
  };

  const goBack = () => {
    history.push(`/migration`);
  }

  return (
    <div className="d-flex flex-row justify-content-start align-items-start" style={{ width: '100%', height: '100vh' }}>
      <NavSidebar />
      <Container fluid style={{ padding: '20px', marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'auto', marginLeft: '20px', marginRight: '20px' }}>
        
        {/* Go Back Button */}
        <div className={`d-flex flex-row flex-no-wrap justify-start align-items-center mb-3`}>
          <Button variant="link" onClick={() => {
            goBack();
          }}>
            <MdArrowBack />
          </Button>
          <div className="font-weight-bold">{'Go Back'}</div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontSize: '26px', fontWeight: 'bold', color: '#2c3e50', margin: 0 }}>Cost Avoidance Metrics</h2>
          
          <div className="d-flex">
            {/* Property Group Dropdown - Show only for Meijer */}
            {hasPropertyLists && (
              <div className="d-flex flex-row mr-3" style={{ alignItems: 'center' }}>
                <label style={{ marginRight: '8px', fontSize: '14px', fontWeight: '500' }}>Property Group:</label>
                <select
                  value={selectedPropertyList?.id || ''}
                  onChange={(e) => {
                    const listId = e.target.value;
                    if (listId) {
                      const list = availablePropertyLists.find(l => l.id.toString() === listId);
                      handlePropertyListSelect(list);
                    } else {
                      setSelectedPropertyList(null);
                      handleViewModeToggle('all');
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    fontSize: '14px',
                    border: '1px solid #dee2e6',
                    borderRadius: '4px',
                    backgroundColor: 'white',
                    cursor: 'pointer',
                    minWidth: '150px'
                  }}
                >
                  <option value="">All Properties</option>
                  {availablePropertyLists.map(list => (
                    <option key={list.id} value={list.id}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* Three-way Toggle: Favorites | Property Group | All Work Orders */}
            <div className="d-flex flex-row mr-3" style={{ alignItems: 'center' }}>
              <button
                onClick={() => handleViewModeToggle('favorited')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: viewMode === 'favorited' ? '#0056b3' : '#f8f9fa',
                  color: viewMode === 'favorited' ? '#fff' : '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px 0 0 4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                  opacity: 1
                }}
              >
                Favorites ({hasFavorites ? (currentUserData?.favorite_properties ? 
                  (() => {
                    try {
                      return JSON.parse(currentUserData.favorite_properties).length || 0;
                    } catch {
                      return 0;
                    }
                  })() : 0) : 0})
              </button>
              {hasPropertyLists && (
                <button
                  onClick={() => handleViewModeToggle('propertyList')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: viewMode === 'propertyList' ? '#0056b3' : '#f8f9fa',
                    color: viewMode === 'propertyList' ? '#fff' : '#6c757d',
                    border: '1px solid #dee2e6',
                    borderLeft: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s'
                  }}
                >
                  Property Group ({selectedPropertyList ? 1 : 0})
                </button>
              )}
              <button
                onClick={() => handleViewModeToggle('all')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: viewMode === 'all' ? '#0056b3' : '#f8f9fa',
                  color: viewMode === 'all' ? '#fff' : '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: hasPropertyLists ? '0 4px 4px 0' : '0 4px 4px 0',
                  borderLeft: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s'
                }}
              >
                All Work Orders
              </button>
            </div>

            <Button 
              variant="outline-primary" 
              className="mr-2"
              onClick={fetchFilteredWorkOrders}
              disabled={isLoading}
            >
              <FaSync className={isLoading ? 'fa-spin mr-1' : 'mr-1'} /> Refresh
            </Button>
            
            <Button 
              variant="outline-success" 
              className="mr-2"
              onClick={handleExportCalculation}
              disabled={isLoading || exportLoading || totalCalculations.count === 0}
            >
              <FaFileExport className="mr-1" /> {exportLoading ? 'Exporting...' : 'Export Calculations'}
            </Button>
            
            <Button 
              variant="outline-info" 
              onClick={handleExportDetails}
              disabled={isLoading || exportLoading || corrigoWorkOrders.length === 0}
            >
              <FaFileExport className="mr-1" /> {exportLoading ? 'Exporting...' : 'Export Details'}
            </Button>
          </div>
        </div>
        
        {/* View Mode Information Messages */}
        {viewMode === 'favorited' && (
          <div className="d-flex justify-content-center mb-2">
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              color: '#004085',
              fontSize: '14px'
            }}>
              {corrigoWorkOrders.length > 0 
                ? `Showing metrics for ${corrigoWorkOrders.length} completed work orders from favorited properties`
                : 'No completed work orders found for favorited properties. You can favorite properties by clicking the star icon on any property card.'
              }
            </div>
          </div>
        )}
        
        {viewMode === 'propertyList' && selectedPropertyList && (
          <div className="d-flex justify-content-center mb-2">
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              color: '#004085',
              fontSize: '14px'
            }}>
              {corrigoWorkOrders.length > 0 
                ? `Showing metrics for ${corrigoWorkOrders.length} completed work orders from property list: ${selectedPropertyList.name}`
                : `No completed work orders found for property list: ${selectedPropertyList.name}`
              }
            </div>
          </div>
        )}
        
        {/* Date Range Picker */}
        <Card className="mb-4 shadow-sm">
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col lg={6} md={12} className="d-flex align-items-center">
                <FaCalendarAlt className="text-primary mr-2" size={20} />
                <h5 className="font-weight-bold mb-0 mr-3">Date Range</h5>
                
                <div className="d-flex align-items-center flex-grow-1">
                  <div className="input-group mr-2">
                    <div className="input-group-prepend">
                      <span className="input-group-text">From</span>
                    </div>
                    <DatePicker
                      onChange={setTempStartDate}
                      value={tempStartDate}
                      className="form-control"
                      format="MM/dd/yyyy"
                      clearIcon={null}
                    />
                  </div>
                  
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">To</span>
                    </div>
                    <DatePicker
                      onChange={setTempEndDate}
                      value={tempEndDate}
                      className="form-control"
                      format="MM/dd/yyyy"
                      clearIcon={null}
                    />
                  </div>
                </div>
              </Col>
              
              <Col lg={6} md={12} className="d-flex align-items-center mt-md-3 mt-lg-0">
                <Button 
                  variant="primary" 
                  onClick={applyDateRange}
                  className="mr-3"
                  disabled={isLoading}
                >
                  <FaFilter className="mr-1" /> Apply
                </Button>
                
                <div className="text-muted">
                  {isLoading ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <>
                      <Badge variant="info" className="mr-2">
                        {corrigoWorkOrders.length} completed work orders
                      </Badge>
                      <small>
                        {moment(startDate).format('MMM DD, YYYY')} - {moment(endDate).format('MMM DD, YYYY')}
                      </small>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Data</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}

        {/* Show empty state for favorited or property list view when no results */}
        {((viewMode === 'favorited' && corrigoWorkOrders.length === 0 && !isLoading) ||
          (viewMode === 'propertyList' && corrigoWorkOrders.length === 0 && !isLoading)) && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#6c757d',
            fontSize: '16px'
          }}>
            <div style={{ marginBottom: '10px' }}>
              {viewMode === 'favorited' ? '🌟 No completed work orders found for your favorited properties!' :
               viewMode === 'propertyList' ? `📋 No completed work orders found for property list: ${selectedPropertyList?.name}` : ''}
            </div>
            <div style={{ fontSize: '14px' }}>
              {viewMode === 'favorited' ? 
                (hasFavorites 
                  ? 'Try adjusting the date range or switch back to "All Work Orders" to see more results.'
                  : 'You can favorite properties by clicking the star icon on any property card in the Inspection page.')
                : 'Try adjusting the date range or switch back to "All Work Orders" to see more results.'
              }
            </div>
          </div>
        )}
        
        {!isLoading && !error && (
          <>
            {/* Cost Avoidance Calculations Card - MOVED TO TOP */}
            <Card className="mb-4 shadow-sm">
              <Card.Header style={{ backgroundColor: '#007bff', color: 'white', borderBottom: 'none' }}>
                <h5 className="mb-0 font-weight-bold">
                  <FaCalendarAlt className="mr-2" />
                  Net Cost Avoidance Calculations
                  {viewMode === 'favorited' && <small className="ml-2">(Favorited Properties Only)</small>}
                  {viewMode === 'propertyList' && selectedPropertyList && <small className="ml-2">({selectedPropertyList.name})</small>}
                </h5>
              </Card.Header>
              <Card.Body style={{ backgroundColor: '#f8f9fa' }}>
                <Row>
                  {/* Copeland Discus */}
                  <Col md={3} className="mb-3">
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e3e6ea',
                      height: '100%'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#6c757d',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        Copeland Discus
                      </div>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: costAvoidanceCalculations.copelandDiscus.cumulative >= 0 ? '#28a745' : '#dc3545',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        ${costAvoidanceCalculations.copelandDiscus.cumulative.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        textAlign: 'center',
                        marginBottom: '12px'
                      }}>
                        Cumulative
                      </div>
                      <div style={{
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        padding: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#495057'
                        }}>
                          {costAvoidanceCalculations.copelandDiscus.count}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d'
                        }}>
                          # of WO's
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Copeland Scroll */}
                  <Col md={3} className="mb-3">
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e3e6ea',
                      height: '100%'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#6c757d',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        Copeland Scroll
                      </div>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: costAvoidanceCalculations.copelandScroll.cumulative >= 0 ? '#28a745' : '#dc3545',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        ${costAvoidanceCalculations.copelandScroll.cumulative.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        textAlign: 'center',
                        marginBottom: '12px'
                      }}>
                        Cumulative
                      </div>
                      <div style={{
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        padding: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#495057'
                        }}>
                          {costAvoidanceCalculations.copelandScroll.count}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d'
                        }}>
                          # of WO's
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Carlyle Reed SHC */}
                  <Col md={3} className="mb-3">
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '1px solid #e3e6ea',
                      height: '100%'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#6c757d',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        Carlyle Reed SHC
                      </div>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: costAvoidanceCalculations.carlyleShc.cumulative >= 0 ? '#28a745' : '#dc3545',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        ${costAvoidanceCalculations.carlyleShc.cumulative.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        textAlign: 'center',
                        marginBottom: '12px'
                      }}>
                        Cumulative
                      </div>
                      <div style={{
                        backgroundColor: '#e9ecef',
                        borderRadius: '4px',
                        padding: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#495057'
                        }}>
                          {costAvoidanceCalculations.carlyleShc.count}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#6c757d'
                        }}>
                          # of WO's
                        </div>
                      </div>
                    </div>
                  </Col>

                  {/* Total */}
                  <Col md={3} className="mb-3">
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '8px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                      border: '2px solid #007bff',
                      height: '100%'
                    }}>
                      <div style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#007bff',
                        marginBottom: '8px',
                        textAlign: 'center'
                      }}>
                        Total
                      </div>
                      <div style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: totalCalculations.cumulative >= 0 ? '#28a745' : '#dc3545',
                        textAlign: 'center',
                        marginBottom: '8px'
                      }}>
                        ${totalCalculations.cumulative.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                      <div style={{
                        fontSize: '12px',
                        color: '#6c757d',
                        textAlign: 'center',
                        marginBottom: '12px'
                      }}>
                        Cumulative
                      </div>
                      <div style={{
                        backgroundColor: '#e3f2fd',
                        borderRadius: '4px',
                        padding: '8px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          fontSize: '18px',
                          fontWeight: 'bold',
                          color: '#007bff'
                        }}>
                          {totalCalculations.count}
                        </div>
                        <div style={{
                          fontSize: '12px',
                          color: '#007bff'
                        }}>
                          # of WO's
                        </div>
                      </div>
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Historical Cost Avoidance Chart - Note: Chart shows all historical data, not filtered by favorites */}
            <Card className="mb-4 shadow-sm">
              <Card.Header style={{ backgroundColor: '#28a745', color: 'white', borderBottom: 'none' }}>
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="mb-0 font-weight-bold">
                      <FaChartBar className="mr-2" />
                      Monthly Net Cost Avoided (Confirmed) - Historical Trend
                    </h5>
                    <small className="text-light">
                      July 2024 - Present
                      {viewMode === 'favorited' && ' (Note: Chart shows all historical data, calculations above show favorited properties only)'}
                      {viewMode === 'propertyList' && ' (Note: Chart shows all historical data, calculations above show property list only)'}
                    </small>
                  </div>
                  <Button
                    variant="light"
                    size="sm"
                    onClick={handleDownloadChart}
                    disabled={chartLoading || chartDownloadLoading}
                    style={{
                      backgroundColor: 'rgba(255, 255, 255, 0.2)',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                      color: 'white'
                    }}
                    className="d-flex align-items-center"
                  >
                    <FaDownload className="mr-1" size={12} />
                    {chartDownloadLoading ? 'Downloading...' : 'Download Chart'}
                  </Button>
                </div>
              </Card.Header>
              <Card.Body style={{ backgroundColor: 'white', padding: '20px' }}>
                {chartLoading ? (
                  <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
                    <Spinner animation="border" role="status" style={{ color: '#28a745', width: '3rem', height: '3rem' }}>
                      <span className="sr-only">Loading chart...</span>
                    </Spinner>
                  </div>
                ) : (
                  <div ref={chartRef} style={{ width: '100%', height: '400px' }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={chartData}
                        margin={{
                          top: 20,
                          right: 30,
                          left: 20,
                          bottom: 5,
                        }}
                      >
                        <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                        <XAxis 
                          dataKey="month" 
                          tick={{ fontSize: 12 }}
                          angle={-45}
                          textAnchor="end"
                          height={80}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => `$${value.toLocaleString()}`}
                        />
                        <Tooltip 
                          formatter={(value, name) => [`$${value.toLocaleString()}`, name]}
                          labelStyle={{ color: '#333' }}
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                        />
                        <Bar 
                          dataKey="Copeland Discus" 
                          fill="#007bff" 
                          name="Copeland Discus"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar 
                          dataKey="Carlyle SHC" 
                          fill="#8A2BE2" 
                          name="Carlyle SHC"
                          radius={[2, 2, 0, 0]}
                        />
                        <Bar 
                          dataKey="Copeland Scroll" 
                          fill="#ffc107" 
                          name="Copeland Scroll"
                          radius={[2, 2, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </Card.Body>
            </Card>
          </>
        )}

        {isLoading && (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '300px' }}>
            <Spinner animation="border" role="status" style={{ color: '#007bff', width: '3rem', height: '3rem' }}>
              <span className="sr-only">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading work orders, please wait...</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default CostAvoidanceMetricsScreen;
